<script setup>
/** SelectGroup */
/* Use for groups of facets in search/browse UIs:
    - in which all facets appear in a dropdown/HTML <select>
    - and the user can only select one at a time
    Example: "Subject" dropdown in Browse Goals and Browse Present Levels
*/
import {
  SelectRoot,
  SelectTrigger,
  SelectValue,
  SelectPortal,
  SelectContent,
  SelectViewport,
  SelectItem,
  SelectItemText,
  SelectSeparator
} from 'reka-ui'
import LockedOption from '@/components/LockedOption.vue'

// reka-ui's SelectItem throws if given a value of "" (it reserves "" to mean
// "no selection"), but real filterGroup data uses value: '' for an "All X" /
// unset option — remap it to a sentinel at the Select boundary so callers
// never see anything but their own value, including ''. Every non-empty value
// is also prefixed so a filterGroup value that happens to collide with the
// sentinel string itself can't be mistaken for the empty option.
const EMPTY_VALUE_SENTINEL = '__select-group-empty__'
const SELECT_VALUE_PREFIX = '__select-group-value__:'
const toSelectValue = (value) => (
  value === ''
    ? EMPTY_VALUE_SENTINEL
    : value === null || value === undefined
      ? value
      : `${SELECT_VALUE_PREFIX}${value}`
)
const fromSelectValue = (value) => {
  if (value === EMPTY_VALUE_SENTINEL) return ''
  return typeof value === 'string' && value.startsWith(SELECT_VALUE_PREFIX)
    ? value.slice(SELECT_VALUE_PREFIX.length)
    : value
}

const props = defineProps({
  // The group of select options: {label: String, value: String, disabled: Boolean, locked: Boolean, separator: Boolean}
  filterGroup: {
    type: Array,
    default: () => [],
    validator: (value) => Array.isArray(value) && value.every(item => 
      typeof item === 'object' && 
      (item.separator || (typeof item.label === 'string' && typeof item.value === 'string'))
    )
  },
  // Label for the dropdown
  groupLabel: {
    type: String,
    required: true
  },
  // Placeholder value
  placeholder: {
    type: String,
    default: 'Select an option'
  },
  // The selected value. v-model in the parent.
  modelValue: {
    type: String,
    default: null
  },
  // Container portal target for modals
  containerPortal: {
    type: String,
    default: undefined
  },
  showLabel: {
    type: Boolean,
    default: true
  },
  // Marks the trigger aria-invalid; callers show the error message themselves
  invalid: {
    type: Boolean,
    default: false
  },
  // The id of the caller's error message, which describes the trigger while it is invalid
  errorMessageId: {
    type: String,
    default: undefined
  },
  // Renders the visible label as a heading of this level instead of a paragraph
  labelHeadingLevel: {
    type: Number,
    default: undefined
  },
  gbTheme: {
    type: String,
    default: 'blue',
    validator: (value) => ['blue', 'purple'].includes(value)
  },
  triggerLabelClasses: {
    type: Array,
    default: () => []
  },
  // Masks the selection and its options from FullStory session replay, for options that are PII
  fsExclude: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const handleSelectValueChange = (value) => emit('update:modelValue', fromSelectValue(value))

// Find the selected item's label to display in the trigger
const getSelectedLabel = () => {
  if (props.modelValue === null || props.modelValue === undefined) return props.placeholder
  if (!props.filterGroup || !Array.isArray(props.filterGroup)) return props.placeholder
  const selectedItem = props.filterGroup.find(item => item.value === props.modelValue)
  return selectedItem?.label || props.placeholder
}

const HOVERED_ITEM_CLASSES = 'hover:bg-gray-300 data-[highlighted]:bg-gray-300'
const SELECTED_ITEM_CLASSES = {
  blue: `bg-primary-400 text-white hover:text-gray-900 data-[highlighted]:text-gray-900 ${HOVERED_ITEM_CLASSES}`,
  purple: `bg-purple-300 text-white hover:text-gray-900 data-[highlighted]:text-gray-900 ${HOVERED_ITEM_CLASSES}`
}
const ITEM_KEYBOARD_FOCUS_RING = 'data-[highlighted]:ring-2 data-[highlighted]:ring-inset data-[highlighted]:ring-black'

const itemStateClasses = (item) => item.value === props.modelValue
  ? SELECTED_ITEM_CLASSES[props.gbTheme] ?? SELECTED_ITEM_CLASSES.blue
  : HOVERED_ITEM_CLASSES
</script>

<template>
  <div class="mb-6 mt-2">
    <component :is="labelHeadingLevel ? `h${labelHeadingLevel}` : 'p'" v-if="showLabel" class="my-2 font-bold uppercase text-gray-900">{{ groupLabel }}</component>
    <SelectRoot :model-value="toSelectValue(modelValue)" @update:model-value="handleSelectValueChange">
      <SelectTrigger
        class="font-normal w-full rounded-xs border border-gray-600 bg-white hover:border-black hover:bg-gray-300 flex items-center justify-between px-3 py-1.5 focus-visible:ring-2 focus-visible:ring-black text-md data-[state=open]:outline data-[state=open]:outline-[3px] data-[state=open]:outline-offset-2 data-[state=open]:outline-gray-900"
        :class="{ 'border-red-300': $attrs.class?.includes('error'), 'fs-exclude': fsExclude }"
        :aria-label="`${groupLabel} selection, ${getSelectedLabel()}`"
        :aria-invalid="invalid || undefined"
        :aria-describedby="invalid ? errorMessageId : undefined"
      >
        <SelectValue :placeholder="placeholder">
          <span :class="triggerLabelClasses">{{ getSelectedLabel() }}</span>
        </SelectValue>
        <span class="material-icons-round text-display-sm text-gray-900" aria-hidden="true">arrow_drop_down</span>
      </SelectTrigger>
      <SelectPortal :to="containerPortal">
        <SelectContent
          position="popper"
          :side-offset="4"
          :aria-label="groupLabel"
          class="w-[--reka-select-trigger-width] bg-white border border-gray-600 rounded-xs shadow-md z-50"
          :class="{ 'fs-exclude': fsExclude }"
        >
          <SelectViewport class="w-full max-h-[--reka-select-content-available-height] overflow-y-auto">
            <template v-for="(item, index) in filterGroup" :key="item.value !== undefined ? item.value : index">
              <SelectItem
                v-if="item.locked"
                :value="toSelectValue(item.value)"
                disabled
                class="py-2 px-4 cursor-not-allowed outline-none"
              >
                <SelectItemText>
                  <LockedOption :label="item.label" />
                </SelectItemText>
              </SelectItem>
              <SelectItem
                v-else-if="item.value !== undefined"
                :value="toSelectValue(item.value)"
                :disabled="item.disabled"
                class="cursor-pointer py-2 px-4 focus:outline-none rounded-none"
                :class="[ITEM_KEYBOARD_FOCUS_RING, itemStateClasses(item)]"
              >
                <SelectItemText>{{ item.label }}</SelectItemText>
              </SelectItem>
              <SelectSeparator v-if="item.separator" class="h-px my-1 bg-gray-400" />
            </template>
          </SelectViewport>
        </SelectContent>
      </SelectPortal>
    </SelectRoot>
  </div>
</template>